import { UnknownAction, Dispatch, PayloadAction, createSlice } from '@reduxjs/toolkit'
import moment from 'moment';
import { ElectionState, ElectionStatus, EraseStatus, Translatable, VoteResult } from '../../_generatedTypes.g';
import { pushNewElectionToServer } from '../../components/election/ElectionItemActions';
import { suggestCode } from '../../Election';
import { suggestName } from '../election/electionsSlice';
export interface HomeScreenState {
    sideMenuVisible: boolean,
    nextCandidacyFrom: string,
    nextCandidacyUntil: string,
    nextVoteOpen: string,
    nextVoteClose: string,
    nextVisibleFrom: string,
    nextVisibleUntil: string,
    useCandidateSystem: boolean,
    nextName: Translatable,
    nextCode: string,
    isNextCodeCustom: boolean,
    errors?: string[],
    justAddedCode: string;
}
export const makeNextElection = (homeScreenState : HomeScreenState, elections : ElectionState[], token : string, dispatch: Dispatch<UnknownAction>): Promise<boolean> => {
    const blueprint = homeScreenState;
    let election = {
        CandidacyFromMoment: moment(blueprint.nextCandidacyFrom).toISOString(),
        CandidacyUntilMoment: moment(blueprint.nextCandidacyUntil).toISOString(),
        VisibleFromMoment: moment(blueprint.nextVisibleFrom).toISOString(),
        VoteOpenMoment: moment(blueprint.nextVoteOpen).toISOString(),
        VoteCloseMoment: moment(blueprint.nextVoteClose).toISOString(),
        VisibleUntilMoment: moment(blueprint.nextVisibleUntil).toISOString(),
        Name: blueprint.nextName,
        CandidateSystem: blueprint.useCandidateSystem,
        Code: blueprint.nextCode,
    };
    return pushNewElectionToServer(election, token, dispatch).then(result => {
        if (result.success) {
            editNextElectionName({ NL: '', EN: '' }, homeScreenState, elections, dispatch);
            dispatch(nextElectionCodeSet({ code: '', isManual: false }));
            dispatch(justAddedElectionCodeSet(election.Code));
            return true;
        }
        return false;
    });
};


export const duplicateElection = (code: string, elections: ElectionState[], hasCandidacy: boolean|null, token: string, dispatch: Dispatch<UnknownAction>) => {
    const original = elections.find(x => x.Code === code)!;
    const allCodes = elections.map(x => x.Code);
    const allNames = elections.map(x => [x.Name.NL, x.Name.EN]).reduce((names, langs) => names.concat(langs), []);;

    let newElection: ElectionState = {
        EraseStatus: EraseStatus.Idle,
        Code: suggestCode(original.Name, allCodes),
        Name: suggestName(original.Name, allNames),
        RowVersion: '',
        Message: original.Message,
        ElectionId: 0,
        VoteResult: VoteResult.NotYetVoted,
        VoterCount: 0,
        VoteCount: 0,
        HasLogo: false,
        ElectionStatus: ElectionStatus.NotYetOpen,
        Description: original.Description,
        RandomOrderParties: original.RandomOrderParties,
        SeatCount: original.SeatCount,
        UserHasVotingRight: original.UserHasVotingRight,
        ActuallyInvisible: original.ActuallyInvisible,
        CandidateSystem: original.CandidateSystem,
        ...setTwoWeeksInFuture(original, hasCandidacy),
    };
    return pushNewElectionToServer(newElection, token, dispatch);
}

type TElectionRange = { CandidacyFromMoment: string, CandidacyUntilMoment: string, VisibleFromMoment: string, VisibleUntilMoment: string, VoteOpenMoment: string, VoteCloseMoment: string };
function setTwoWeeksInFuture(original: TElectionRange, hasCandidacy: boolean|null) {
    const inTwoWeeks = moment(new Date()).add(2, 'weeks');

    const atLeastTwoWeeksAhead = function (date: string) {
        return moment.max(moment(date, moment.ISO_8601), inTwoWeeks).toISOString()
    };
    return {
        CandidacyFromMoment: hasCandidacy ? atLeastTwoWeeksAhead(original.CandidacyFromMoment) : moment(new Date(1, 0, 1)).toISOString(),
        CandidacyUntilMoment: hasCandidacy ? atLeastTwoWeeksAhead(original.CandidacyUntilMoment) : moment(new Date(1, 0, 1)).toISOString(),
        VisibleFromMoment: atLeastTwoWeeksAhead(original.VisibleFromMoment),
        VisibleUntilMoment: atLeastTwoWeeksAhead(original.VisibleUntilMoment),
        VoteOpenMoment: atLeastTwoWeeksAhead(original.VoteOpenMoment),
        VoteCloseMoment: atLeastTwoWeeksAhead(original.VoteCloseMoment),
    };
}
export const editNextElectionName = (name: Partial<Translatable>, homeScreen : HomeScreenState, elections : ElectionState[], dispatch: Dispatch<UnknownAction>) => {
    dispatch(nextElectionNameEdited(name));

    const nextName = homeScreen.nextName;
    if (!homeScreen.isNextCodeCustom) {
        const code = suggestCode(nextName, elections.map(x => x.Code));
        dispatch(nextElectionCodeSet({ code: code, isManual: false }));
    }
};

const homeSlice = createSlice({
    name: 'SLICE_home',
    initialState: {
        sideMenuVisible: false,
        nextCandidacyFrom: moment(new Date()).startOf('day').add(1, 'weeks').toDate().toISOString(),
        nextCandidacyUntil: moment(new Date()).startOf('day').add(2, 'weeks').toDate().toISOString(),
        nextVisibleFrom: moment(new Date()).startOf('day').add(3, 'weeks').toDate().toISOString(),
        nextVoteOpen: moment(new Date()).startOf('day').add(5, 'weeks').toDate().toISOString(),
        nextVoteClose: moment(new Date()).endOf('day').add(6, 'weeks').toDate().toISOString(),
        nextVisibleUntil: moment(new Date()).endOf('day').add(7, 'weeks').toDate().toISOString(),
        nextName: { EN: '', NL: '' },
        nextCode: '',
        isNextCodeCustom: false,
        useCandidateSystem: false,
        justAddedCode: '',
    } as HomeScreenState,
    reducers: {
        systemSet(state, action: PayloadAction<boolean>) {
            state.useCandidateSystem = action.payload;
        },
        nextElectionNameEdited(state, action: PayloadAction<Partial<Translatable>>) {
           state.nextName = { NL: state.nextName.NL, EN: state.nextName.EN, ...action.payload }
           },
        nextCandidacyFromSet(state, action: PayloadAction<string>) {
            state.nextCandidacyFrom = action.payload;
        },
        nextCandidacyUntilSet(state, action: PayloadAction<string>) {
            state.nextCandidacyUntil = action.payload;
        },
        nextVisibleFromSet(state, action: PayloadAction<string>) {
            state.nextVisibleFrom = action.payload;
        },
        nextVisibleUntilSet(state, action: PayloadAction<string>) {
            state.nextVisibleUntil = action.payload;
        },
        nextVoteOpenSet(state, action: PayloadAction<string>) {
            state.nextVoteOpen = action.payload;
        },
        nextVoteCloseSet(state, action: PayloadAction<string>) {
            state.nextVoteClose = action.payload;
        },
        createElectionSidePanelToggled(state, action: PayloadAction<undefined>) {
            state.sideMenuVisible = !state.sideMenuVisible;
        },
        nextElectionCodeSet(state, action: PayloadAction<{ code: string, isManual: boolean }>) {
            state.nextCode = action.payload.code;
            state.isNextCodeCustom = action.payload.isManual;
        },
        newElectionErrorsSet(state, action: PayloadAction<string[]>) {
            state.errors = action.payload;
        },
        justAddedElectionCodeSet(state, action: PayloadAction<string>) {
            state.justAddedCode = action.payload;
        },
    },
})

export const {
    systemSet,
    nextElectionNameEdited,
    nextCandidacyFromSet,
    nextCandidacyUntilSet,
    nextVisibleFromSet,
    nextVisibleUntilSet,
    nextVoteOpenSet,
    nextVoteCloseSet,
    createElectionSidePanelToggled,
    nextElectionCodeSet,
    newElectionErrorsSet,
    justAddedElectionCodeSet,
} = homeSlice.actions

export default homeSlice.reducer
